import { HelpOutlineIcon, VisibilityIcon, VisibilityOffIcon } from '@assets/icons';
import {
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TextField.scss';

const AppTextField: FC<AppTextFieldProps> = ({
  id,
  label,
  required,
  disabled,
  placeholder,
  value,
  defaultValue,
  type,
  isRow = false,
  autoFocus,
  maxLength,
  onChange,
  error,
  icon,
  enableTrim,
  multiline,
  tooltip,
}) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const isTypePassword = useMemo(() => type === 'password', [type]);

  const inputType = useMemo(() => {
    if (!isTypePassword) return type;
    return showPassword ? 'text' : 'password';
  }, [type, showPassword]);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('message.MSE00001', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  const handleShowPassword = () => setShowPassword((show) => !show);

  const handleMouseIcon = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleTrimOnBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange?: React.ChangeEventHandler<HTMLInputElement>,
  ) => {
    if (!enableTrim) return;
    const trimmedValue = event.target.value.trim();
    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: trimmedValue,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    if (onChange) {
      onChange(newEvent);
    }
  };

  return (
    <Grid
      container
      className="TextField"
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems="center"
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
          textAlign={isRow ? 'right' : undefined}
        >
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
          {tooltip && (
            <Tooltip title={t(tooltip)} placement="top-start">
              <HelpOutlineIcon className="TooltipIcon" />
            </Tooltip>
          )}
        </Grid>
      )}
      <Grid item xs={8}>
        <TextField
          id={id}
          name={id}
          disabled={disabled}
          type={inputType}
          placeholder={t(placeholder!)}
          value={value}
          defaultValue={defaultValue}
          autoFocus={autoFocus}
          multiline={multiline}
          InputProps={{
            endAdornment: !isTypePassword ? (
              icon ? (
                <InputAdornment position="end">
                  <IconButton tabIndex={-1} aria-label="icon-input" edge="end">
                    {icon}
                  </IconButton>
                </InputAdornment>
              ) : undefined
            ) : (
              <InputAdornment position="end">
                <IconButton
                  tabIndex={-1}
                  aria-label="toggle password visibility"
                  onClick={handleShowPassword}
                  onMouseDown={handleMouseIcon}
                  onMouseUp={handleMouseIcon}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{ maxLength: isTypePassword ? 32 : maxLength }}
          onChange={onChange}
          onBlur={(event) => handleTrimOnBlur(event, onChange)}
          error={!!error?.type}
          helperText={errorText}
        />
      </Grid>
    </Grid>
  );
};

export default AppTextField;
